/**
 * TripMaster public tourism surface (agent-5413).
 *
 * Dedicated Amplify builds set VITE_APP_SURFACE=tripmaster.
 * Hostname matching covers www.tripmaster.co.il while DNS still
 * points at a shared HotelsRiver deploy.
 */

export const TRIPMASTER_APP_SURFACE = "tripmaster";
export const TRIPMASTER_PUBLIC_SITE_TOKEN = "agent-5413";
export const TRIPMASTER_CONTACT_EMAIL = "fly@tripmaster.co.il";
export const TRIPMASTER_HOME_PATH = "/";
export const TRIPMASTER_CANONICAL_ORIGIN = "https://www.tripmaster.co.il";
export const TRIPMASTER_HOSTS = Object.freeze(["www.tripmaster.co.il", "tripmaster.co.il"]);
export const TRIPMASTER_SURFACE_DOM_ATTR = "data-tm-surface";
const HOTELSRIVER_AGENCY_SESSION_KEYS = Object.freeze([
  "sessionID",
  "UserInfo",
  "agentId",
  "agentLastActivityTime",
]);

function readEnvValue(env, key) {
  const bag = env && typeof env === "object" ? env : {};
  return String(bag[key] ?? "").trim();
}

export function readAppSurface(env = import.meta.env) {
  return readEnvValue(env, "VITE_APP_SURFACE").toLowerCase();
}

export function isTripMasterDedicatedBuild(env = import.meta.env) {
  return readAppSurface(env) === TRIPMASTER_APP_SURFACE;
}

export function normalizeHostname(hostname) {
  return String(hostname || "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");
}

export function isTripMasterHost(hostname) {
  return TRIPMASTER_HOSTS.includes(normalizeHostname(hostname));
}

export function resolveTripMasterHostname(hostname) {
  if (hostname != null) return hostname;
  if (typeof window === "undefined") return "";
  return window.location?.hostname || "";
}

export function readTripMasterSurfaceMarker(doc) {
  const root = doc || (typeof document !== "undefined" ? document : null);
  return root?.documentElement?.getAttribute(TRIPMASTER_SURFACE_DOM_ATTR) === "1";
}

export function isTripMasterSurface({ hostname, env, document: doc } = {}) {
  if (isTripMasterDedicatedBuild(env)) return true;
  if (readTripMasterSurfaceMarker(doc)) return true;
  return isTripMasterHost(resolveTripMasterHostname(hostname));
}

/** Operations desk lives on /ops and keeps its own login session. */
export function isTripMasterOpsPath(pathname) {
  const path = String(pathname || "").split("?")[0].replace(/\/+$/, "") || "/";
  return path === "/ops" || path.startsWith("/ops/");
}

/**
 * Public TripMaster pages drop HotelsRiver agency keys.
 * /ops keeps the operations login so a refresh does not sign the desk out.
 */
export function shouldStripTripMasterAgencySession(pathname, options) {
  if (!isTripMasterSurface(options)) return false;
  return !isTripMasterOpsPath(pathname);
}

/** Drop leftover HotelsRiver agency sessions so TM `/` never becomes /login. */
export function stripHotelsRiverAgencySession(storage, options) {
  if (!isTripMasterSurface(options)) return false;
  const store = storage || (typeof localStorage !== "undefined" ? localStorage : null);
  if (!store?.removeItem) return false;
  for (const key of HOTELSRIVER_AGENCY_SESSION_KEYS) {
    try {
      store.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  return true;
}

export function resolveTripMasterPublicSiteToken({ hostname, env } = {}) {
  if (!isTripMasterSurface({ hostname, env })) return null;
  return TRIPMASTER_PUBLIC_SITE_TOKEN;
}
